import { Suspense, lazy, useEffect } from "react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import SkipLink from "./components/SkipLink";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ConsentBanner from "./components/ConsentBanner";
import RequireAuth from "./auth/RequireAuth";
import RequireAdmin from "./auth/RequireAdmin";
import RequireOnboarding from "./features/onboarding/RequireOnboarding";
import { OnboardingStateProvider } from "./features/onboarding/OnboardingStateContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider } from "./auth/AuthContext";
import { APP_NAME } from "./lib/branding";
import { captureError, initErrorMonitoring, type ErrorFeature } from "./lib/monitoring/errorMonitor";

// Eagerly loaded - needed for initial render
import Landing from "./pages/Landing";
import Waitlist from "./pages/Waitlist";

const isWaitlistMode = import.meta.env.VITE_WAITLIST_MODE === 'true';

// Lazy loaded pages - split into separate chunks
const PublicQuiz = lazy(() => import("./pages/PublicQuiz"));
const QuizResults = lazy(() => import("./pages/QuizResults"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Goals = lazy(() => import("./pages/Goals"));
const AuthSignup = lazy(() => import("./pages/AuthSignup"));
const AuthSignin = lazy(() => import("./pages/AuthSignin"));
const AuthReset = lazy(() => import("./pages/AuthReset"));
const AuthConfirm = lazy(() => import("./pages/AuthConfirm"));
const DebugHealth = lazy(() => import("./pages/DebugHealth"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Contact = lazy(() => import("./pages/Contact"));
const Healthz = lazy(() => import("./pages/Healthz"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Insights = lazy(() => import("./pages/Insights"));
const SettingsProfile = lazy(() => import("./pages/SettingsProfile"));
const SettingsBilling = lazy(() => import("./pages/SettingsBilling"));
const BillingSuccess = lazy(() => import("./pages/billing/Success"));
const BillingRunbook = lazy(() => import("./pages/admin/BillingRunbook"));
const ErrorMonitor = lazy(() => import("./pages/admin/ErrorMonitor"));
const AiUsage = lazy(() => import("./pages/admin/AiUsage"));
const ActivationEvents = lazy(() => import("./pages/admin/ActivationEvents"));
const Callback = lazy(() => import("./pages/auth/Callback"));
const FutureMe = lazy(() => import("./pages/FutureMe"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Cashflow = lazy(() => import("./pages/Cashflow"));
const DebtHub = lazy(() => import("./pages/DebtHub"));

/** Map a react-query key to the feature tag used in monitoring. */
function featureFromKey(key: unknown): ErrorFeature {
  const first = Array.isArray(key) ? String(key[0] ?? '') : '';
  if (first.includes('transaction')) return 'transactions';
  if (first.includes('goal')) return 'goals';
  if (first.includes('wishlist')) return 'wishlist';
  if (first.includes('debt')) return 'debt';
  if (first.includes('billing') || first.includes('subscription')) return 'billing';
  if (first.includes('future')) return 'future-me';
  if (first.includes('settings') || first.includes('profile')) return 'settings';
  return 'unknown';
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      void captureError({ feature: featureFromKey(query.queryKey), error, severity: 'warning', context: { kind: 'query' } });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      void captureError({ feature: featureFromKey(mutation.options.mutationKey), error, context: { kind: 'save' } });
    },
  }),
});

// Minimal loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const AppRoutesWrapper = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = APP_NAME;
  }, [location.pathname]);

  const hideChrome = (isWaitlistMode && location.pathname === '/') || location.pathname === '/legal/privacypolicy';
  
  return (
    <>
      {!hideChrome && <Header />}
      <div className="min-h-screen text-foreground">
        <RequireOnboarding>
          <ErrorBoundary>
            <Suspense key={location.pathname} fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={isWaitlistMode ? <Waitlist /> : <Landing />} />
                <Route path="/quiz" element={<PublicQuiz />} />
                <Route path="/quiz/results" element={<QuizResults />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/auth/signup" element={<AuthSignup />} />
                <Route path="/auth/signin" element={<AuthSignin />} />
                <Route path="/auth/reset" element={<AuthReset />} />
                <Route path="/auth/confirm" element={<AuthConfirm />} />
                <Route path="/auth/callback" element={<Callback />} />
                <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                <Route path="/insights" element={<RequireAuth><Insights /></RequireAuth>} />
                <Route path="/future-me" element={<RequireAuth><FutureMe /></RequireAuth>} />
                <Route path="/transactions" element={<RequireAuth><Transactions /></RequireAuth>} />
                <Route path="/goals" element={<RequireAuth><Goals /></RequireAuth>} />
                <Route path="/wishlist" element={<RequireAuth><Wishlist /></RequireAuth>} />
                <Route path="/cashflow" element={<RequireAuth><Cashflow /></RequireAuth>} />
                <Route path="/debt" element={<RequireAuth><DebtHub /></RequireAuth>} />
                <Route path="/goals/wishlist" element={<Navigate to="/wishlist" replace />} />
                <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
                <Route path="/settings/profile" element={<RequireAuth><SettingsProfile /></RequireAuth>} />
                <Route path="/settings/billing" element={<RequireAuth><SettingsBilling /></RequireAuth>} />
                <Route path="/billing/success" element={<RequireAuth><BillingSuccess /></RequireAuth>} />
                {/* Every admin route (current and future) is gated by this single fail-closed guard */}
                <Route path="/admin" element={<RequireAuth><RequireAdmin><Outlet /></RequireAdmin></RequireAuth>}>
                  <Route index element={<Navigate to="/admin/errors" replace />} />
                  <Route path="errors" element={<ErrorMonitor />} />
                  <Route path="ai-usage" element={<AiUsage />} />
                  <Route path="activation" element={<ActivationEvents />} />
                  <Route path="billing-runbook" element={<BillingRunbook />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
                <Route path="/debug/health" element={<RequireAuth><DebugHealth /></RequireAuth>} />
                <Route path="/legal/privacy" element={<Privacy />} />
                <Route path="/legal/privacypolicy" element={<PrivacyPolicy />} />
                <Route path="/legal/terms" element={<Terms />} />
                <Route path="/legal/cookies" element={<CookiePolicy />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/healthz" element={<Healthz />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </RequireOnboarding>
      </div>
      {!hideChrome && <Footer />}
    </>
  );
};

const App = () => {
  useEffect(() => initErrorMonitoring(), []);

  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter
            future={{
              v7_relativeSplatPath: true,
            }}
          >
            <OnboardingStateProvider>
              <ScrollToTop />
              <SkipLink />
              <AppRoutesWrapper />
              <ConsentBanner />
              <SpeedInsights />
            </OnboardingStateProvider>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
